import React from 'react';
import { format, parseISO } from 'date-fns';
import { usePreApprovalMetrics } from '../context/PreApprovalMetricsContext';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)  }M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)  }K`;
  }
  return num.toLocaleString();
}

function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export function DocumentProcessingTable() {
  const { byDocumentUser, isLoading } = usePreApprovalMetrics();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (byDocumentUser.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No document processing activity found in the selected date range
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead className="text-center">Total</TableHead>
            <TableHead className="text-center">Documents</TableHead>
            <TableHead className="text-center">Pages</TableHead>
            <TableHead className="text-center">Characters</TableHead>
            <TableHead className="text-center">Completed</TableHead>
            <TableHead className="text-center">Failed</TableHead>
            <TableHead className="text-center">Avg Time</TableHead>
            <TableHead>Last Processed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {byDocumentUser.map((user) => (
            <TableRow key={user.user_id || 'auto-queue'}>
              <TableCell className="font-medium">
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-amber-600">
                  {formatDuration(user.total_extraction_time_ms)}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-slate-600">{user.documents_processed}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-blue-600">{formatNumber(user.total_pages_extracted)}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-purple-600">{formatNumber(user.total_chars_extracted)}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-green-600">{user.completed}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-red-600">{user.failed}</span>
              </TableCell>
              <TableCell className="text-center">
                <span className="font-semibold text-amber-600">{formatDuration(user.avg_extraction_time_ms)}</span>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {user.last_processed_at
                  ? format(parseISO(user.last_processed_at), 'MMM d, yyyy h:mm a')
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default DocumentProcessingTable;
